import { Card, Group, JsonInput, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { BackgroundJobType } from '@repo/api-codegen';
import { apiQueryKey, useApiCreateBackgroundJob } from '@repo/api-codegen';
import {
	Button,
	ListBox,
	Switch,
	Text,
	TextInput,
	Title,
} from '@repo/foundations';
import { useState } from 'react';
import { queryClient } from '../../../api';
import { useStyles } from '../IntegrationsStatusPage/IntegrationsStatusPage.styles';

function BackgroundJobCreate() {
	const { classes } = useStyles();
	const { mutateAsync: createBackgroundJob } = useApiCreateBackgroundJob();

	// Type
	const [title, setTitle] = useState<string>('');
	const [type, setType] = useState<BackgroundJobType>('indexing');
	const [opened, setOpened] = useState<boolean>(false);
	const [useBatch, setUseBatch] = useState<boolean>(false);
	const [batchMemoryMb, setBatchMemoryMb] = useState<number>(512);
	const [functionId, setFunctionId] = useState<string>('');
	const [functionArgs, setFunctionArgs] = useState<string>('');

	// Config
	const [config, setConfig] = useState('');

	// TOTP
	const [totp, setTotp] = useState<string>('');

	// Create
	const handleCreateBackgroundJob = () => {
		// Validate TOTP
		if (!totp || totp.trim() === '') {
			showNotification({
				title: 'TOTP Required',
				message: 'Please provide a valid TOTP code',
				color: 'red',
			});
			return;
		}

		let parsedConfig;
		try {
			parsedConfig = JSON.parse(config || '{}');
			parsedConfig.function_id = functionId;
			parsedConfig.function_args = JSON.parse(functionArgs || '{}');
		} catch {
			showNotification({
				title: 'Invalid JSON',
				message: 'Please provide a valid JSON',
				color: 'red',
			});
			return;
		}
		createBackgroundJob(
			{
				body: {
					title: title,
					type: type,
					config: parsedConfig,
					totp: totp,
					run_on_batch: useBatch,
					batch_memory_mb: useBatch ? batchMemoryMb : undefined,
				},
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: apiQueryKey('admin/background_job'),
					});
					showNotification({
						title: 'Job created',
						message: 'Yay',
						color: 'green',
					});
					// Clear TOTP after successful creation
					setTotp('');
				},
				onError: (error) => {
					const errorMessage =
						('detail' in error && error.detail) ||
						"I didn't spend too much time here, look at network tab to debug";
					showNotification({
						title: 'Failed to create job',
						message: errorMessage,
						color: 'red',
					});
				},
			}
		);
	};

	return (
		<Card className={classes.card}>
			<Stack spacing="sm">
				<Title order={2}>Create new background job</Title>
				<TextInput
					label="Job title"
					value={title}
					onChange={(e) => setTitle(e.currentTarget.value)}
				/>
				<Group>
					<Text size="sm" weight="semibold">
						Job type
					</Text>
					<ListBox
						opened={opened}
						onOpenChange={(newOpened) => setOpened(newOpened)}
						closeOnEscape
					>
						<ListBox.Target>
							<Button onClick={() => setOpened(!opened)}>{type}</Button>
						</ListBox.Target>
						<ListBox.ItemsDropdown
							items={['indexing', 'bg_admin'] as BackgroundJobType[]}
							renderItem={(item) => (
								<ListBox.Item
									onClick={() => {
										setType(item);
										setOpened(false);
									}}
								>
									{item}
								</ListBox.Item>
							)}
						/>
					</ListBox>
					<Switch
						label="Use Batch"
						checked={useBatch}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							setUseBatch(e.currentTarget.checked)
						}
					/>
					{useBatch && (
						<TextInput
							label="Batch Memory MB"
							value={batchMemoryMb.toString()}
							onChange={(e) => setBatchMemoryMb(Number(e.currentTarget.value))}
							w="100px"
						/>
					)}
				</Group>
				<TextInput
					label="Function ID"
					value={functionId}
					onChange={(e) => setFunctionId(e.currentTarget.value)}
				/>
				<JsonInput
					label="Function Args"
					value={functionArgs}
					onChange={(value) => {
						setFunctionArgs(value);
					}}
					minRows={8}
					formatOnBlur
					validationError="Invalid JSON"
					placeholder={
						'// JSON function args object here. Maps to the arguments of the function. JSON format like this: \n{\n   "ids": ["id_1"]\n}'
					}
				/>
				<Stack w="100%">
					<JsonInput
						label="Extra Configs"
						value={config}
						onChange={(value) => {
							setConfig(value);
						}}
						placeholder={
							'// JSON config object here. Check BackgroundJobConfig for validation syntax \n{\n"workspace_ids": ["id_1"]\n}'
						}
						validationError="Invalid JSON"
						formatOnBlur
						minRows={2}
					/>
				</Stack>
				<TextInput
					label="TOTP Code"
					placeholder="Enter TOTP code"
					value={totp}
					onChange={(e) => setTotp(e.currentTarget.value)}
					description="Two-factor authentication code required for security"
				/>
				<Button variant="primary" onClick={handleCreateBackgroundJob} size="sm">
					Create new
				</Button>
			</Stack>
		</Card>
	);
}

export default BackgroundJobCreate;
